import React from 'react';
import {v4 as uuid} from 'uuid';
import {Stack, Box} from '@mui/material';
import {
  Button,
  Body,
  designSystemToken,
  Icon,
  Tooltip,
} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useProcessEvent from '@contentlab/hooks/analytics/use-process-event';
import {FeatureName, ProjectStatus} from '@contentlab/types/project';
import {ProjectProps} from '@contentlab/types/projectProps';
import downloadProject from '@contentlab/utils/downloadProject';
import {
  CONTENT_LAB_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';

const getCaptionsZipTooltip = () => {
  const zipContents = [
    {
      title: translate('SRT file'),
      subtitle: translate('Open subtitles'),
      titleColor: designSystemToken('semantic.fg.primary'),
      subtitleColor: designSystemToken('semantic.fg.inverse-secondary'),
      icon: (
        <Icon
          name="Social-InstagramReels"
          size="small"
          appearance="neutral"
          color={designSystemToken('semantic.fg.primary')}
        />
      ),
    },
    {
      title: translate('Burned-in video'),
      subtitle: translate('Subtitles embedded in the image'),
      titleColor: designSystemToken('semantic.fg.primary'),
      subtitleColor: designSystemToken('semantic.fg.inverse-secondary'),
      icon: (
        <Icon
          name="Navigation-Terms"
          size="small"
          appearance="neutral"
          color={designSystemToken('semantic.fg.primary')}
        />
      ),
    },
  ];

  return (
    <Stack gap="12px" sx={{maxWidth: '280px'}}>
      <Body size="sm" color={designSystemToken('semantic.fg.primary')}>
        {translate("What's inside the ZIP?")}
      </Body>
      {zipContents.map((item) => (
        <Stack
          key={item.title}
          direction="row"
          gap="8px"
          alignItems="flex-start"
        >
          <Box
            sx={{
              minWidth: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: '2px',
            }}
          >
            {item.icon}
          </Box>
          <Stack gap="2px" flex={1}>
            <Body size="sm" color={item.titleColor}>
              {item.title}
            </Body>
            <Body size="sm" color={item.subtitleColor}>
              {item.subtitle}
            </Body>
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
};

function DownloadProjectButton({
  project,
  isDisabled,
  downloadInProgress,
  setDownloadInProgress,
}: {
  project: ProjectProps;
  isDisabled: boolean;
  downloadInProgress: boolean;
  setDownloadInProgress: (downloadInProgress: boolean) => void;
}) {
  const {started, ended} = useProcessEvent();
  const {uiInteraction} = useUiInteractionEvent();

  return (
    <Tooltip
      title={
        project.feature === FeatureName.CAPTIONS
          ? getCaptionsZipTooltip()
          : null
      }
      placement="top"
      arrow
    >
      <Box
        sx={{
          'display': 'inline-flex',
          '& button': {
            paddingLeft: '24px !important',
            paddingRight: '24px !important',
          },
        }}
      >
        <Button
          appearance="neutral"
          mode="filled"
          size="large"
          icon={
            <Icon
              name="Actions-LooksOff"
              size="medium"
              appearance="neutral"
              color={
                isDisabled
                  ? designSystemToken('semantic.fg.disabled')
                  : designSystemToken('semantic.fg.neutral-secondary')
              }
            />
          }
          isLoading={downloadInProgress}
          disabled={isDisabled || downloadInProgress}
          onClick={async () => {
            await onClickDownloadProject(
              project,
              downloadInProgress,
              setDownloadInProgress,
              {started, ended},
              uiInteraction
            );
          }}
        >
          {translate('Download project')}
        </Button>
      </Box>
    </Tooltip>
  );
}

async function onClickDownloadProject(
  project: ProjectProps,
  downloadInProgress: boolean,
  setDownloadInProgress: (downloadInProgress: boolean) => void,
  processEvent: {started: (params: any) => void; ended: (params: any) => void},
  uiInteraction: (params: any) => void
) {
  if (downloadInProgress) {
    return;
  }
  const interactionId = uuid();
  uiInteraction({
    item_name: 'download_project',
    interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECT,
    flow_name: FLOW_NAMES.CONTENT_LAB.PROJECT,
    asset_id: project.id,
    interaction_id: interactionId,
  });
  if (project.status !== ProjectStatus.READY) {
    return;
  }
  try {
    setDownloadInProgress(true);
    await downloadProject(
      project.id,
      interactionId,
      CONTENT_LAB_VIEW_NAMES.PROJECT,
      processEvent
    );
  } finally {
    setDownloadInProgress(false);
  }
}

export default DownloadProjectButton;
